// 📈 Trading › Dashboard — resumen de la operación del bot (cuenta global).
// KPIs vía RPC trading.resumen() + últimos trades y eventos (SELECT sobre las tablas
// del esquema trading, que app_user puede leer). Solo lectura; refresca cada 15s.
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, Loader2, Activity, ShieldAlert, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const TR = { headers: { 'Accept-Profile': 'trading', 'Content-Profile': 'trading' } };

const money = (v: number | null | undefined) => {
  const n = Number(v);
  if (v === null || v === undefined || Number.isNaN(n)) return '—';
  try { return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'USD' }).format(n); }
  catch { return `$${n.toFixed(2)}`; }
};
const pnlCls = (v: number | null | undefined) => (Number(v) > 0 ? 'text-green-600' : Number(v) < 0 ? 'text-red-600' : '');

interface Resumen {
  ambiente: string; activo: boolean; kill_switch: boolean; symbol: string;
  balance_usd: number | null; equity_usd: number | null; heartbeat: string | null;
  posiciones_abiertas: number | null; pnl_total: number; pnl_dia: number; trades: number;
  ultimo_trade: { symbol: string; pnl: number | null; ts_cierre: string | null } | null;
}
interface Trade {
  id: string; symbol: string; lado: string; pnl: number | null; pnl_pct: number | null;
  precio_entrada: number | null; precio_salida: number | null; motivo_cierre: string | null;
  entorno: string; ts_cierre: string | null;
}
interface Evento { id: number; tipo: string; nivel: string; symbol: string | null; mensaje: string; ts: string; }

function Stat({ label, valor, clase }: { label: string; valor: React.ReactNode; clase?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`mt-1 text-xl font-semibold ${clase || ''}`}>{valor}</p>
      </CardContent>
    </Card>
  );
}

export function TradingDashboard() {
  const { data: r, isLoading } = useQuery<Resumen>({
    queryKey: ['trading-resumen'],
    queryFn: async () => (await apiClient.post('/rpc/resumen', {}, TR)).data,
    refetchInterval: 15000,
  });
  const { data: trades = [] } = useQuery<Trade[]>({
    queryKey: ['trading-ultimos-trades'],
    queryFn: async () => (await apiClient.get('/trades', { ...TR, params: { order: 'ts_cierre.desc.nullslast', limit: 10 } })).data || [],
    refetchInterval: 15000,
  });
  const { data: eventos = [] } = useQuery<Evento[]>({
    queryKey: ['trading-ultimos-eventos'],
    queryFn: async () => (await apiClient.get('/eventos', { ...TR, params: { order: 'ts.desc', limit: 12 } })).data || [],
    refetchInterval: 15000,
  });

  if (isLoading || !r) return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const hb = r.heartbeat ? new Date(r.heartbeat) : null;
  const online = hb ? (Date.now() - hb.getTime()) < 5 * 60 * 1000 : false;
  const nivelCls = (n: string) => (n === 'error' ? 'text-red-600' : n === 'warning' ? 'text-amber-600' : 'text-muted-foreground');

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <LayoutDashboard className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Dashboard Trading</h1>
          <p className="text-sm text-muted-foreground">Resumen de la operación del bot.</p>
        </div>
      </div>

      {r.kill_switch && (
        <div className="flex items-center gap-2 rounded-md border border-red-500/40 bg-red-50/40 px-4 py-2 text-sm text-red-600 dark:bg-red-900/10">
          <ShieldAlert className="h-4 w-4" /> Kill switch ACTIVO — el bot no está operando (reactivación manual en Configuración).
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="flex items-center gap-1"><Activity className={`h-4 w-4 ${online ? 'text-green-600' : 'text-muted-foreground'}`} />
          Motor: <b>{online ? 'en línea' : 'sin latido'}</b>{hb && <span className="text-muted-foreground"> · {hb.toLocaleString(LOCALE)}</span>}</span>
        <span className="text-muted-foreground">|</span>
        <span>Ambiente: <b className="uppercase">{r.ambiente}</b></span>
        <span className="text-muted-foreground">|</span>
        <span>Estado: <b>{r.activo ? 'activo' : 'detenido'}</b></span>
        <span className="text-muted-foreground">|</span>
        <span>Par: <b>{r.symbol}</b></span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Balance (USDT libre)" valor={money(r.balance_usd)} />
        <Stat label="Equity estimado" valor={money(r.equity_usd)} />
        <Stat label="PnL total" valor={money(r.pnl_total)} clase={pnlCls(r.pnl_total)} />
        <Stat label="PnL del día" valor={money(r.pnl_dia)} clase={pnlCls(r.pnl_dia)} />
        <Stat label="Posiciones abiertas" valor={r.posiciones_abiertas ?? 0} />
        <Stat label="Trades cerrados" valor={r.trades} />
        <Stat label="Último trade" valor={r.ultimo_trade ? money(r.ultimo_trade.pnl) : '—'} clase={pnlCls(r.ultimo_trade?.pnl)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Últimos trades */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Últimos trades</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Cierre</th><th className="py-2 pr-3">Par</th>
                  <th className="py-2 pr-3 text-right">PnL</th><th className="py-2 pr-3 text-right">%</th>
                  <th className="py-2 pr-3">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {trades.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin trades todavía.</td></tr>}
                {trades.map((t) => (
                  <tr key={t.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap text-xs">{t.ts_cierre ? new Date(t.ts_cierre).toLocaleString(LOCALE) : <span className="text-primary">abierta</span>}</td>
                    <td className="py-1.5 pr-3">{t.symbol}</td>
                    <td className={`py-1.5 pr-3 text-right whitespace-nowrap flex items-center justify-end gap-1 ${pnlCls(t.pnl)}`}>
                      {Number(t.pnl) > 0 ? <TrendingUp className="h-3 w-3" /> : Number(t.pnl) < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                      {t.pnl == null ? '—' : money(t.pnl)}</td>
                    <td className={`py-1.5 pr-3 text-right ${pnlCls(t.pnl)}`}>{t.pnl_pct == null ? '—' : `${Number(t.pnl_pct).toFixed(2)}%`}</td>
                    <td className="py-1.5 pr-3 text-xs text-muted-foreground">{t.motivo_cierre || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Últimos eventos */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Actividad reciente</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Hora</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Mensaje</th>
                </tr>
              </thead>
              <tbody>
                {eventos.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin actividad todavía.</td></tr>}
                {eventos.map((e) => (
                  <tr key={e.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap text-xs">{new Date(e.ts).toLocaleTimeString(LOCALE)}</td>
                    <td className={`py-1.5 pr-3 text-xs font-medium ${nivelCls(e.nivel)}`}>{e.tipo}</td>
                    <td className="py-1.5 pr-3 text-xs"><span className="inline-block max-w-80 truncate align-middle" title={e.mensaje}>{e.mensaje}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
